import { faKeyboard } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextField from '@mui/material/TextField';
import { BrInput } from '@govbr-ds/react-components'
import { readOnlyModeAtom } from 'contexts/FeatureContext';
import { useAtom } from 'jotai';
import useSubmitKskFormOnShiftEnter from 'ksk/hooks/useSubmitKskFormOnShiftEnter';
import { useBuildInputId } from 'ksk/styledComponents/components/KskForm/KskForm';
import React from 'react';
import { Controller, get } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { componentErrorMsg } from 'utils/utils';
import KskInputHelper from '../KskInputHelper/KskInputHelper';
import KskTooltipWrapper from '../KskPopover/KskTooltipWrapper';
import InputWithMask from './InputWithMask';

const defaultCustomNumberFormatProps = {
  integerScale: 14,
}

const defaultNumberFormatProps = {
  thousandSeparator: '.',
  decimalSeparator: ',',
  thousandsGroupStyle: 'thousand',
  decimalScale: 2,
  //fixedDecimalScale: '2',
  allowNegative: true,
  allowEmptyFormatting: false,
  isNumericString: true,
  displayType: 'input',
  type: 'text',
  //prefix: '',
  //suffix: '',
  //value: null,
  //defaultValue: null,
  //format:
  //removeFormatting: (formattedValue) => numericString
  //mask:
  //renderText
  //getInputRef
}

const NEGATIVE_VALUE_CLASS_NAME = 'numeric-input-negative-value';

export const KskInput = React.forwardRef(({
  autocomplete = 'off',
  label,
  labelIcon = faKeyboard,
  name,
  id, 
  submitOnEnter = false,
  //onKeyPress,
  form,
  tooltip,
  errors,
  className,
  required,
  optional,
  multiline,
  control,
  mask,
  unmask,
  numberFormat,
  helperText,
  alwaysShowHelperText,
  inputProps,
  onKeyDown,
  disableReadOnlyContext = false,
  upperCase = false,
  maintainMaskedValue = false,
  // propriedades de controle
  onChange, onPaste, onMouseDown, onFocus, onBlur, value, disabled, readOnly, maxLength,
  //  
  // defaultValue='',
  ...otherProps
}, ref) => {
  

  const defaultValue = form?.watch(name) ?? '';

  // Helper: considera preenchido para string não vazia, 0, objetos (valueAsObject), arrays com itens
  const isFilled = (v) => {
    if (v === 0) return true;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === 'object') return Object.keys(v).length > 0;
    return v != null && String(v).trim() !== '';
  };

  const currentValue = form?.watch?.(name);
  const shrinkByValue = isFilled(currentValue);

  const errorsObj = form?.formState?.errors || errors;
  const _isSubmitted = form?.formState?.isSubmitted ?? true;
  const hasErrors = _isSubmitted && (errorsObj ? !!get(errorsObj, name) : false);  //const handleSubmitOnEnter = handleSubmitOnEnterBuilder(submitOnEnter || multiline, onKeyPress);
  const dirtyClass = form?.formState?.dirtyFields?.[name] ? ' is-dirty' : '';
  const touchedClass = form?.formState?.touchedFields?.[name] ? ' is-touched' : '';
  const _id = useBuildInputId({id, name});
  
  const _control = control || form?.control;
  const [readOnlyMode] = useAtom(readOnlyModeAtom);
  const _readOnly = (!disableReadOnlyContext) && (readOnly || readOnlyMode);

  const onKeyDownHandler =  useSubmitKskFormOnShiftEnter({ preventEnter: (!submitOnEnter&&!multiline), onKeyDown });

  if (mask && numberFormat) {
    throw new Error(
      componentErrorMsg({
        componentName: 'KskInput', 
        name, id: _id, 
        message: `As propriedades "mask" e "numberFormat" são autoexcludentes. ` +
        `Use apenas uma delas, nunca as duas ao mesmo tempo.`
      })
    );
  }

  const labelWithIcon = (
    <>
      { !(disabled || _readOnly) && <FontAwesomeIcon icon={labelIcon}/> }
      <span> {label}</span>
    </>
  );

  const registerProps = form ? form.register(name) : {};

  const mergedInputProps = {
    ...inputProps,
    ...(maxLength ? { maxLength } : {}),
    ...(upperCase ? { style: { textTransform: 'uppercase', ...inputProps?.style } } : {})
  };

  const inputLabelProps = shrinkByValue
    ? { ...otherProps.InputLabelProps, shrink: true }
    : otherProps.InputLabelProps;

  const textFieldProps = {
    id:_id,
    label: labelWithIcon,
    className:`${className}${dirtyClass}${touchedClass}`,
    // defaultValue,
    onKeyDown: onKeyDownHandler,
    autoComplete: autocomplete,
    multiline,
    error: hasErrors,
    helperText: <KskInputHelper {...{optional, required, hasErrors, name, errorsObj, helperText, readOnly:disabled || _readOnly}} />,
    disabled,
    inputProps: mergedInputProps,
    ...otherProps,
    ...(inputLabelProps && { InputLabelProps: inputLabelProps })
  };

  const defaultInput = () => {
    // Se o form está disponível e não há onChange/value controlados externamente,
    // usar register do react-hook-form para conectar o campo
    const registration = (form && !onChange && value === undefined) ? form.register(name) : {};
    const controlledProps = (onChange || value !== undefined) ? {onChange, value} : {};
    
    return (
      <TextField name={name} 
        inputRef={registration.ref || ref}
        {...textFieldProps} 
        {...controlledProps}
        {...registration}
        {...{onFocus}}
        InputProps={{
          ...otherProps?.InputProps,
          onPaste,
          onMouseDown,
          onFocus,
          onBlur,
          disabled,
          readOnly: _readOnly
        }}
      />
    );
  };

  // Avaliar no futuro 
  const maskedInput2 = () => (
    <Controller
      control={_control}
      name={name}
      defaultValue={defaultValue}
      render={({ field }) => (
        <InputWithMask 
          mask={mask} 
          unmask={unmask}
          upperCase={upperCase}
          maintainMaskedValue={maintainMaskedValue}
          defaultValue={form?.watch(name) ?? ''}
          inputProps={ {...textFieldProps}}
          onBlur={e => { (onBlur && onBlur(e)); field.onBlur(e)}}
          onChange={e => { (onChange && onChange(e)); field.onChange(e)}}
          {...{onPaste, onMouseDown, onFocus, value, disabled: disabled || _readOnly, }}
          ref={field.ref}
          readOnly= {_readOnly}/>
      )}
    />
  );

  const numberInput = () => {
    const params = {...defaultCustomNumberFormatProps, ...numberFormat};
    const intScale = params?.integerScale;
    const isIntScaleValidFn = (values) => values?.value?.split('.')[0].length <= intScale;
    const allowNegativeFn = params?.allowNegative
      ? () => true
      : (values) => values?.value?.indexOf('-') < 0

    // Override do comportamento original do componente
    // pois se já estivesse negativo na hora do carregamento, o input transformava o valor
    // em um número positivo, o que não é um comportamento adequado.  
    const isAllowedFn = numberFormat?.isAllowed
      ? (values) => numberFormat.isAllowed(values) && isIntScaleValidFn(values) && allowNegativeFn(values)
      : (values) => isIntScaleValidFn(values) && allowNegativeFn(values);

    const {integerScale, ..._numberFormat} = numberFormat; 
    const numberFormatProps = {..._numberFormat, isAllowed: isAllowedFn, allowNegative: true}

    return (
      <Controller
        control={_control}
        name={name}
        defaultValue={defaultValue}

        render={({ field }) => {
          const {
            onChange: _onChange,
            onBlur: _onBlur,
            value,
            ref,
          } = field;

          return (
            <NumericFormat
              customInput={TextField}
              {...textFieldProps} 
              {...{name, onFocus, disabled: disabled || _readOnly }}
              value={value ?? ''}
              onValueChange={(values) => {
                const nextValue = values.floatValue ?? null;
                _onChange(nextValue);
                if (onChange) {
                  onChange({ target: { name, value: nextValue } });
                }
              }}
              onBlur={(e) => {
                _onBlur();
                if (onBlur) {
                  onBlur(e);
                }
              }}
              InputProps={{
                ...otherProps?.InputProps,
                ...numberFormatProps?.InputProps,
                onPaste,
                onMouseDown,
                onFocus,
                disabled,
                readOnly: _readOnly
              }}
              inputProps={{...{maxLength}}}
              {...{...defaultNumberFormatProps, ...numberFormatProps}}
              getInputRef={ref}
            />
          );
        }}
      />
  )};

  const renderedInput = mask 
    ? maskedInput2() 
    : numberFormat 
      ? numberInput()
      : defaultInput();

  return (
    tooltip 
      ? <KskTooltipWrapper tooltipProps={tooltip}>{renderedInput}</KskTooltipWrapper>
      : renderedInput
  );   
});


export const KskInputSimples = React.forwardRef(({
  autocomplete = 'off',
  className,
  name,
  phone,
  tablet,
  desktop,
  errors,
  ...otherProps
}, ref) => {
  
  const deviceClass = (deviceName, cols) => Number(cols) ? `${deviceName}-${cols}`: '';
  const deviceClasses = [deviceClass('phone', phone), deviceClass('tablet', tablet), deviceClass('desktop', desktop)].join(' ');   

  const classes = `${className} ${deviceClasses}`
 
  return (
    <TextField
      name={name}
      ref={ref}
      className={classes}
      autoComplete={autocomplete}
      error={!!errors?.[name]}
      helperText={errors?.[name]?.message || JSON.stringify(errors || {limpo: 'limpo'})}
      {...otherProps}
    /> 
  ) 
});

export default KskInput;